import React, { useState, useEffect, useCallback, useRef } from 'react';
import { clickEffectTypeMap } from '@typings/setting/mouseSet.interface';

// 색상을 rgba 형식으로 변환하는 유틸리티 함수
const convertToRgba = (color: string, alpha: number = 1): string => {
  // CSS 변수 처리: var(--primaryColor) 형태
  if (color.startsWith('var(')) {
    const match = color.match(/var\((--[A-Za-z0-9_-]+)\)/);
    if (match) {
      const varName = match[1];
      // :root 우선, 없으면 body에서 조회
      const fromRoot = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      const fromBody = getComputedStyle(document.body).getPropertyValue(varName).trim();
      const resolved = fromRoot || fromBody;
      if (resolved) {
        return convertToRgba(resolved, alpha);
      }
    }
    // 변수 해석 실패 시 안전 폴백 색상
    return `rgba(255, 99, 132, ${alpha})`;
  }

  // 이미 rgba 형식인 경우 alpha만 변경
  if (color.startsWith('rgba(')) {
    const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
    if (rgbaMatch) {
      const [, r, g, b] = rgbaMatch;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  // rgb 형식인 경우 alpha 추가
  if (color.startsWith('rgb(')) {
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const [, r, g, b] = rgbMatch;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  // hex 형식인 경우 rgba로 변환 (3/6자리 지원)
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const full = hex.length === 3
      ? hex.split('').map((ch) => ch + ch).join('')
      : hex;
    const r = parseInt(full.substr(0, 2), 16);
    const g = parseInt(full.substr(2, 2), 16);
    const b = parseInt(full.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // CSS 변수나 기타 형식인 경우 폴백 처리: 그대로 사용 시 캔버스 오류가 나므로 안전 색상 반환
  return `rgba(255, 99, 132, ${alpha})`;
};

interface Sparkle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
}

interface ClickEffect {
  id: number;
  x: number;
  y: number;
  type: keyof typeof clickEffectTypeMap;
  color: string;
  color2?: string;
  size: number;
  duration: number;
  startTime: number;
  progress: number;
  particles?: Particle[];
  sparkles?: Sparkle[]; // 반짝이 효과 추가
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
}

interface ClickEffectsProps {
  effectType: keyof typeof clickEffectTypeMap;
  isActive: boolean;
  color: string;
  color2: string;
  size: number;
  duration: number;
}

function ClickEffects({
  effectType,
  isActive,
  color,
  color2,
  size,
  duration
}: ClickEffectsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const nextHasActiveRef = useRef<boolean>(false);
  const fadeTimerRef = useRef<number | null>(null);
  const [effects, setEffects] = useState<ClickEffect[]>([]);
  const [canvasOpacity, setCanvasOpacity] = useState<number>(0);

  // Canvas 초기화 및 리사이즈 처리
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, []);

  // 파티클 생성 함수 (변환된 색상을 전달받아 사용)
  const createParticles = (x: number, y: number, effectSize: number, c1: string, c2: string): Particle[] => {
    const particles: Particle[] = [];
    const particleCount = 18; // 12 → 18로 증가 (50% 더 많은 파티클)

    for (let i = 0; i < particleCount; i++) {
      const angle = (i * 20) * Math.PI / 180; // 30도 → 20도 간격으로 조정
      const speed = effectSize * 0.02;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: i % 2 === 0 ? c1 : c2,
        size: effectSize / 6, // 초기 크기를 약간 더 크게 (8 → 6)
      });
    }

    return particles;
  };

  // 반짝이 생성 함수 (별이펙트형용)
  const createSparkles = (x: number, y: number, effectSize: number, duration: number): Sparkle[] => {
    const sparkles: Sparkle[] = [];
    const sparkleCount = 8; // 8개의 반짝이

    for (let i = 0; i < sparkleCount; i++) {
      const angle = Math.random() * Math.PI * 2; // 랜덤 방향
      // 파동 속도와 동기화: 
      // 파동 최종 크기 = size * 4 * progress (progress=1일 때)
      // 반짝이 최종 거리 = speed * progress * duration
      // 동기화를 위해: speed * duration ≈ size * 1.5 (파동 대비 적당한 거리)
      const targetDistance = effectSize * 8; // 파동 대비 적당한 거리로 조정 (2 → 1.5)
      const speed = (targetDistance + Math.random() * targetDistance * 0.2) / duration; // 랜덤 변화도 줄임 (0.3 → 0.2)

      sparkles.push({
        x: x + Math.cos(angle) * (effectSize * 0.1), // 시작 위치 분산
        y: y + Math.sin(angle) * (effectSize * 0.1),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1 + Math.random() * 3, // 크기
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3, // 회전 속도
        opacity: 0.8 + Math.random() * 0.2, // 초기 투명도
      });
    }

    return sparkles;
  };

  // 클릭 이펙트 생성
  const createEffect = useCallback((e: MouseEvent) => {
    if (!isActive) return;
    // 새 이펙트 시작 시, 즉시 캔버스 보이도록
    setCanvasOpacity(1);
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }

    // 입력 색상 값 정규화 (CSS 변수/hex/rgb → rgba)
    const resolvedColor = convertToRgba(color, 1);
    const resolvedColor2 = convertToRgba(color2 || color, 1);

    const newEffect: ClickEffect = {
      id: Date.now() + Math.random(),
      x: e.clientX,
      y: e.clientY,
      type: effectType,
      color: resolvedColor,
      color2: resolvedColor2,
      size,
      duration,
      startTime: Date.now(),
      progress: 0,
      particles: effectType === 'particle' ? createParticles(e.clientX, e.clientY, size, resolvedColor, resolvedColor2) : undefined,
      sparkles: effectType === 'star' ? createSparkles(e.clientX, e.clientY, size, duration) : undefined,
    };

    setEffects(prev => [...prev, newEffect]);
  }, [isActive, effectType, color, color2, size, duration]);

  // Ripple 이펙트 렌더링 (기존 단순 파동형)
  const drawRipple = (ctx: CanvasRenderingContext2D, effect: ClickEffect) => {
    const { x, y, color, size, progress } = effect;
    // 더 멀리 퍼져나가도록 배율 증가 (4 → 6)
    const currentSize = size * progress * 6;

    // 더 부드럽게 사라지도록 opacity 계산 수정
    let opacity;
    if (progress < 0.6) {
      // 처음 60%까지는 천천히 투명해짐
      opacity = 1 - (progress * 0.4);
    } else if (progress < 0.85) {
      // 60%~85%까지는 중간 속도로 투명해짐
      opacity = 0.76 - ((progress - 0.6) / 0.25) * 0.5;
    } else {
      // 마지막 15%에서 완전히 사라짐
      opacity = 0.26 - ((progress - 0.85) / 0.15) * 0.26;
    }

    // 잔상 방지를 위해 최소 임계값 설정
    if (opacity < 0.05) {
      return; // 매우 투명한 경우 아예 그리지 않음
    }

    ctx.save();
    ctx.globalAlpha = Math.max(0, opacity);
    ctx.strokeStyle = convertToRgba(color, 1);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, currentSize, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  };

  // 별이펙트형 렌더링 - 화사한 빛 효과
  const drawStar = (ctx: CanvasRenderingContext2D, effect: ClickEffect) => {
    const { x, y, color, color2, size, progress, sparkles } = effect;

    // 1. 중심 빛나는 원 (핵심 빛)
    const coreSize = size * (0.3 + progress * 0.7); // 작게 시작해서 커짐
    const coreOpacity = Math.max(0, (1 - progress) * 0.9); // 확실히 0 이상 유지

    if (coreOpacity > 0.005) { // 임계값을 더욱 낮게 설정 (0.01 → 0.005)
      ctx.save();
      ctx.globalAlpha = coreOpacity;

      // 중심 빛나는 그라디언트
      const coreGradient = ctx.createRadialGradient(x, y, 0, x, y, coreSize);
      coreGradient.addColorStop(0, convertToRgba(color, 1.0));
      coreGradient.addColorStop(0.3, convertToRgba(color, 0.8));
      coreGradient.addColorStop(0.7, convertToRgba(color2 || color, 0.4));
      coreGradient.addColorStop(1, 'transparent');

      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(x, y, coreSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // 2. 일렁이는 빛 파동 (3개 층)
    for (let i = 0; i < 3; i++) {
      const waveDelay = i * 0.15;
      const waveProgress = Math.max(0, progress - waveDelay);

      if (waveProgress > 0) {
        const waveSize = size * (1 + i * 0.4) * waveProgress * 4;
        // 투명도 계산 개선: 음수 방지 및 확실한 0 도달
        const baseOpacity = (0.6 - i * 0.15);
        const waveOpacity = Math.max(0, (1 - waveProgress) * baseOpacity);
        // 전체 진행도에 따른 전역 페이드(첫 파동 시작부터 마지막 크기까지 1->0)
        const overallFade = Math.max(0, 1 - progress);
        const finalWaveOpacity = waveOpacity * overallFade;

        if (finalWaveOpacity > 0.005) { // 임계값을 더욱 낮게 설정 (0.01 → 0.005)
          ctx.save();
          ctx.globalAlpha = finalWaveOpacity;

          // 일렁이는 효과를 위한 사인파 변형
          const shimmer = Math.sin(progress * Math.PI * 6 + i * Math.PI) * 0.1;
          const actualWaveSize = waveSize * (1 + shimmer);

          // 파동 그라디언트
          const waveGradient = ctx.createRadialGradient(x, y, actualWaveSize * 0.7, x, y, actualWaveSize);
          waveGradient.addColorStop(0, 'transparent');
          waveGradient.addColorStop(0.7, convertToRgba(color, 0.3));
          waveGradient.addColorStop(0.9, convertToRgba(color2 || color, 0.6));
          waveGradient.addColorStop(1, 'transparent');

          ctx.fillStyle = waveGradient;
          ctx.beginPath();
          ctx.arc(x, y, actualWaveSize, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
    }

    // 3. 반짝이 효과
    if (sparkles) {
      ctx.save();

      sparkles.forEach(sparkle => {
        // 반짝이 위치 업데이트
        const currentX = sparkle.x + sparkle.vx * progress * effect.duration;
        const currentY = sparkle.y + sparkle.vy * progress * effect.duration;
        const currentRotation = sparkle.rotation + sparkle.rotationSpeed * progress * effect.duration;

        // 반짝이 크기와 투명도
        const sparkleProgress = Math.min(progress * 1.5, 1); // progress 제한으로 1을 넘지 않도록
        const sparkleOpacity = sparkle.opacity * (1 - sparkleProgress) * (1 - sparkleProgress); // 확실히 0에 도달
        const sparkleSize = sparkle.size * Math.max(0, 1 - progress * 0.8); // 크기도 확실히 0에 도달

        if (sparkleOpacity > 0.01) { // 임계값을 더욱 낮게 설정 (0.01 → 0.005)
          ctx.save();
          ctx.globalAlpha = sparkleOpacity;
          ctx.translate(currentX, currentY);
          ctx.rotate(currentRotation);

          // 십자가 모양 반짝이
          ctx.strokeStyle = convertToRgba(color, 1.0);
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';

          // 세로선
          ctx.beginPath();
          ctx.moveTo(0, -sparkleSize);
          ctx.lineTo(0, sparkleSize);
          ctx.stroke();

          // 가로선
          ctx.beginPath();
          ctx.moveTo(-sparkleSize, 0);
          ctx.lineTo(sparkleSize, 0);
          ctx.stroke();

          // 작은 중심 원
          ctx.fillStyle = convertToRgba(color2 || color, 1.0);
          ctx.beginPath();
          ctx.arc(0, 0, sparkleSize * 0.3, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
        }
      });

      ctx.restore();
    }
  };

  // Particle 이펙트 렌더링
  const drawParticles = (ctx: CanvasRenderingContext2D, effect: ClickEffect) => {
    if (!effect.particles) return;

    const { progress, duration } = effect;
    const opacity = 1 - progress;

    ctx.save();
    ctx.globalAlpha = opacity;

    effect.particles.forEach(particle => {
      // 파티클 위치 업데이트
      const currentX = particle.x + particle.vx * progress * duration;
      const currentY = particle.y + particle.vy * progress * duration;

      // 파티클 크기: 처음에는 길다가 점점 짧아짐
      const lengthFactor = 1 - progress * 0.8; // 처음 1.0에서 0.2까지 감소
      const currentWidth = particle.size * lengthFactor;
      const currentHeight = particle.size * 3 * lengthFactor; // 세로로 긴 타원형

      ctx.fillStyle = particle.color;
      ctx.beginPath();

      // 타원형 파티클 그리기 (처음에는 길고, 점점 작아짐)
      ctx.ellipse(
        currentX,
        currentY,
        currentWidth,  // 가로 반지름
        currentHeight, // 세로 반지름 (더 김)
        Math.atan2(particle.vy, particle.vx) + Math.PI / 2, // 중심에서 바깥쪽으로 향하는 방향 + 90도 회전
        0,
        Math.PI * 2
      );
      ctx.fill();
    });

    ctx.restore();
  };

  // Multi Ripple 이펙트 렌더링 (3개의 링)
  const drawMultiRipple = (ctx: CanvasRenderingContext2D, effect: ClickEffect) => {
    const { x, y, color, color2, size, progress } = effect;

    for (let i = 0; i < 3; i++) {
      const delay = i * 0.2;
      const adjustedProgress = Math.max(0, progress - delay);
      const currentSize = size * (1 + i * 0.3) * adjustedProgress * 3;
      // 투명도 계산 개선: 음수 방지 및 확실한 0 도달
      const opacity = Math.max(0, (1 - adjustedProgress) * 0.8);

      if (adjustedProgress > 0 && opacity > 0.005) { // 임계값 추가로 잔상 방지
        ctx.save();
        ctx.globalAlpha = opacity;

        // 테두리
        ctx.strokeStyle = convertToRgba(color, 1);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, currentSize, 0, Math.PI * 2);
        ctx.stroke();

        // 배경 채우기 - convertToRgba 사용
        ctx.fillStyle = convertToRgba(color2 || color, 0.2);
        ctx.fill();

        ctx.restore();
      }
    }
  };

  // Pulse 이펙트 렌더링
  const drawPulse = (ctx: CanvasRenderingContext2D, effect: ClickEffect) => {
    const { x, y, color, color2, size, progress } = effect;
    let currentSize, opacity;

    if (progress < 0.5) {
      // 0 -> 1.5배 크기로 확장
      currentSize = size * (progress * 3);
      opacity = 1 - progress * 0.4;
    } else {
      // 1.5배 -> 3배 크기로 확장
      currentSize = size * (1.5 + (progress - 0.5) * 3);
      opacity = 0.6 - (progress - 0.5) * 1.2;
    }

    ctx.save();
    ctx.globalAlpha = Math.max(0, opacity);

    // 방사형 그라디언트 생성 - convertToRgba 사용
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, currentSize);
    gradient.addColorStop(0, convertToRgba(color, 0.8));
    gradient.addColorStop(0.5, convertToRgba(color2 || color, 0.4));
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, currentSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  // 애니메이션 렌더링
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 캔버스 클리어
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentTime = Date.now();

    // 이펙트들 렌더링 및 업데이트
    nextHasActiveRef.current = false;
    setEffects(prevEffects => {
      const activeEffects = prevEffects.filter(effect => {
        const elapsed = currentTime - effect.startTime;
        const progress = Math.min(elapsed / effect.duration, 1);

        effect.progress = progress;

        // 이펙트 렌더링
        switch (effect.type) {
          case 'ripple':
            drawRipple(ctx, effect);
            break;
          case 'particle':
            drawParticles(ctx, effect);
            break;
          case 'ripple2':
            drawPulse(ctx, effect);
            break;
          case 'star':
            drawStar(ctx, effect);
            break;
        }

        // 애니메이션이 완료되지 않은 이펙트만 유지
        // progress < 1 또는 시각적으로 완전히 투명해진 경우 제거
        const isVisuallyComplete = progress >= 0.99; // 99% 완료시 제거
        const keep = progress < 1 && !isVisuallyComplete;
        return keep;
      });
      nextHasActiveRef.current = activeEffects.length > 0;
      return activeEffects;
    });

    // 다음 프레임 요청
    if (nextHasActiveRef.current || isActive) {
      animationFrameRef.current = requestAnimationFrame(render);
    } else {
      // 이펙트가 모두 종료된 경우, 서서히 페이드아웃
      setCanvasOpacity(0);
      // 페이드가 끝난 뒤 버퍼를 완전 초기화
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
      }
      fadeTimerRef.current = window.setTimeout(() => {
        const c = canvasRef.current;
        if (!c) return;
        const cctx = c.getContext('2d');
        if (!cctx) return;
        cctx.save();
        cctx.setTransform(1, 0, 0, 1, 0, 0);
        cctx.clearRect(0, 0, c.width, c.height);
        cctx.restore();
        // 비트맵 자체 리셋으로 혹시 모를 잔상 완전 제거
        const cw = c.width;
        const ch = c.height;
        c.width = cw;
        c.height = ch;
      }, 220); // CSS 트랜지션 시간과 일치
    }
  }, [effects, isActive, color, color2]);

  // 애니메이션 루프 시작/중지
  useEffect(() => {
    if (isActive && effects.length > 0) {
      animationFrameRef.current = requestAnimationFrame(render);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [effects, isActive, render]);

  // 클릭 이벤트 리스너
  useEffect(() => {
    if (isActive) {
      document.addEventListener('click', createEffect);
      return () => document.removeEventListener('click', createEffect);
    }
  }, [createEffect, isActive]);

  // 캔버스 초기화
  useEffect(() => {
    const cleanup = initCanvas();
    return cleanup;
  }, [initCanvas]);

  // 언마운트/변경 시 타이머 정리
  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, []);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: canvasOpacity,
        transition: 'opacity 220ms ease-out',
      }}
    />
  );
}

export default ClickEffects; 